// NOTE: The fetchSuggestions / resolvePlace / formatPrediction / readFormattableText /
// readMatches helpers below are duplicated in vanilla JS at
// themes/default/grunt/js/common/address-search.js (used by /my-journeys/route/<ID>).
// Changes to the Places API surface, field mask, language/region settings, or defensive
// parsing here should be mirrored there to prevent drift. The duplication exists because
// the React app (CRA) and the legacy Grunt/Browserify bundle have no shared import path.

import { isNewPlacesApiEnabled } from './places-flag'

export type PredictionMatch = {
  startOffset: number
  endOffset: number
}

export type PlaceSuggestion = {
  placeId: string
  primaryText: string
  secondaryText: string
  primaryMatches: PredictionMatch[]
}

export type ResolvedAddressComponent = {
  types: string[]
  longText: string
  shortText: string
}

export type ResolvedPlace = {
  placeId: string
  displayName: string
  location: google.maps.LatLng
  addressComponents: ResolvedAddressComponent[]
}

// Bundles all suggestion calls plus the one detail call for a search interaction
// into one billed Places "session"; rotated after a successful detail fetch.
let currentSessionToken: AutocompleteSessionTokenInstance | null = null
const predictionsBySession = new Map<string, PlacePrediction>()

// Test-only: drops the in-memory session token + prediction cache so each test starts fresh.
export function __resetSessionForTests(): void {
  currentSessionToken = null
  predictionsBySession.clear()
}

// Returns autocomplete suggestions for an input string (new API only; legacy mode delegates to Google's bundled <Autocomplete> widget instead).
export async function fetchSuggestions(input: string): Promise<PlaceSuggestion[]> {
  if (!isNewPlacesApiEnabled()) {
    // TODO(NZTAJP-583): remove with UseNewPlacesApi flag — legacy mode never calls this
    // (the legacy <Autocomplete> renders Google's own dropdown directly).
    return []
  }
  if (!input) return []
  try {
    // @types/google.maps doesn't yet cover the new AutocompleteSuggestion API, so we cast
    // through unknown to the local types declared at the bottom of this file.
    const placesNs = google.maps.places as unknown as PlacesNewNamespace
    if (!currentSessionToken) {
      currentSessionToken = new placesNs.AutocompleteSessionToken()
    }
    const { suggestions } = await placesNs.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      includedRegionCodes: ['nz'],
      language: 'en-NZ',
      sessionToken: currentSessionToken,
    })
    // Normalise each SDK suggestion into our PlaceSuggestion shape, skipping ones we can't use.
    const out: PlaceSuggestion[] = []
    for (const suggestion of suggestions) {
      const prediction = suggestion.placePrediction
      // Skip predictions we can't resolve later (no placeId) or can't display (no usable text).
      if (!prediction?.placeId) continue
      const formatted = formatPrediction(prediction)
      if (!formatted) continue
      out.push({ placeId: prediction.placeId, ...formatted })
      // Cache the raw prediction so resolveViaPlace can call prediction.toPlace(),
      // which keeps the details fetch in the same billed session as the suggestions.
      predictionsBySession.set(prediction.placeId, prediction)
    }
    return out
  } catch (err) {
    // Fail soft: a network blip or SDK error shouldn't break typing — just hide the dropdown.
    console.error('Places autocomplete fetchSuggestions failed', err)
    return []
  }
}

// Resolves a place_id into a fully populated ResolvedPlace, dispatching to the legacy or new SDK based on the flag.
export async function resolvePlace(placeId: string): Promise<ResolvedPlace | null> {
  if (!placeId) return null
  if (isNewPlacesApiEnabled()) {
    return resolveViaPlace(placeId)
  }
  // TODO(NZTAJP-583): remove with UseNewPlacesApi flag — legacy PlacesService.getDetails path
  return resolveViaGetDetails(placeId)
}

// --- New path: Place.fetchFields ---

// New-API resolver: constructs a Place and calls fetchFields with the minimum field set the app actually reads.
async function resolveViaPlace(placeId: string): Promise<ResolvedPlace | null> {
  try {
    const placesNs = google.maps.places as unknown as PlacesNewNamespace
    const cached = predictionsBySession.get(placeId)
    const place =
      cached && typeof cached.toPlace === 'function'
        ? cached.toPlace()
        : new placesNs.Place({ id: placeId })
    await place.fetchFields({ fields: ['id', 'displayName', 'location', 'addressComponents'] })
    if (!place.location) return null
    const resolved: ResolvedPlace = {
      placeId: place.id ?? placeId,
      displayName: place.displayName ?? '',
      location: place.location,
      addressComponents: (place.addressComponents ?? []).map((c) => ({
        types: c.types ?? [],
        longText: c.longText ?? '',
        shortText: c.shortText ?? '',
      })),
    }
    // A successful detail fetch consumes the current session — rotate so the
    // next interaction starts a fresh one.
    currentSessionToken = null
    predictionsBySession.clear()
    return resolved
  } catch (err) {
    console.error('Places resolveViaPlace failed', err)
    return null
  }
}

// --- Legacy path: PlacesService.getDetails ---
// TODO(NZTAJP-583): remove with UseNewPlacesApi flag — entire legacy block

let legacyPlacesService: google.maps.places.PlacesService | null = null

// Legacy resolver: lazily reuses a singleton PlacesService to call getDetails, then normalises into the unified shape.
function resolveViaGetDetails(placeId: string): Promise<ResolvedPlace | null> {
  return new Promise((resolve) => {
    if (!legacyPlacesService) {
      legacyPlacesService = new google.maps.places.PlacesService(document.createElement('div'))
    }
    legacyPlacesService.getDetails(
      {
        placeId,
        fields: ['address_component', 'geometry', 'name', 'place_id'],
      },
      (place) => {
        if (!place) {
          resolve(null)
          return
        }
        resolve(normalisePlaceResult(place))
      },
    )
  })
}

// TODO(NZTAJP-583): remove with UseNewPlacesApi flag — legacy → unified shape
// Converts a legacy PlaceResult into the unified ResolvedPlace shape so downstream code stays flag-agnostic.
export function normalisePlaceResult(p: google.maps.places.PlaceResult): ResolvedPlace | null {
  if (!p.geometry?.location) return null
  return {
    placeId: p.place_id ?? '',
    displayName: p.name ?? '',
    location: p.geometry.location,
    addressComponents: (p.address_components ?? []).map((c) => ({
      types: c.types ?? [],
      longText: c.long_name ?? '',
      shortText: c.short_name ?? '',
    })),
  }
}

type FormattedPrediction = {
  primaryText: string
  secondaryText: string
  primaryMatches: PredictionMatch[]
}

// Extracts displayable primary/secondary text (and the match offsets to bold) from a prediction.
// Predictions usually arrive with structuredFormat split into mainText (primary, dark) and
// secondaryText (grey). When structuredFormat is absent we fall back to splitting the full
// formatted text on its first comma — recovering the legacy widget's primary/secondary look.
function formatPrediction(prediction: PlacePrediction): FormattedPrediction | null {
  // Typical case: structuredFormat gave us primary + secondary already split.
  const primaryText = readFormattableText(prediction.structuredFormat?.mainText)
  if (primaryText) {
    return {
      primaryText,
      secondaryText: readFormattableText(prediction.structuredFormat?.secondaryText),
      primaryMatches: readMatches(prediction.structuredFormat?.mainText),
    }
  }

  // Fallback: only the full formatted address is available — we split it ourselves.
  const fullText = readFormattableText(prediction.text)
  if (!fullText) return null
  const fullMatches = readMatches(prediction.text)
  const commaIndex = fullText.indexOf(',')

  // No comma: single-line prediction, bold against the whole string.
  if (commaIndex === -1) {
    return { primaryText: fullText, secondaryText: '', primaryMatches: fullMatches }
  }

  // Comma found: text before is primary, text after is secondary.
  return {
    primaryText: fullText.slice(0, commaIndex).trim(),
    secondaryText: fullText.slice(commaIndex + 1).trim(),
    // Drop match offsets that fall past the comma — they'd point outside the trimmed primary.
    primaryMatches: fullMatches.filter((match) => match.endOffset <= commaIndex),
  }
}

// Defensively extracts a PredictionMatch[] from a possibly-class-instance, possibly-plain-object FormattableText-like value.
function readMatches(value: unknown): PredictionMatch[] {
  if (!value || typeof value !== 'object') return []
  const obj = value as { matches?: unknown }
  if (!Array.isArray(obj.matches)) return []
  const out: PredictionMatch[] = []
  for (const m of obj.matches) {
    if (!m || typeof m !== 'object') continue
    const { startOffset, endOffset } = m as { startOffset?: unknown; endOffset?: unknown }
    if (typeof startOffset === 'number' && typeof endOffset === 'number' && endOffset > startOffset) {
      out.push({ startOffset, endOffset })
    }
  }
  return out
}

// FormattableText may arrive as a plain { text } object, a class instance whose
// text is a getter, or a string-like value with toString(). Read defensively.
function readFormattableText(value: unknown): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    const obj = value as { text?: unknown; toString?: () => string }
    if (typeof obj.text === 'string') return obj.text
    if (typeof obj.toString === 'function') {
      const s = obj.toString()
      if (s && s !== '[object Object]') return s
    }
  }
  return ''
}

// --- Narrow local types for the new Places API ---
// @types/google.maps 3.58.x does not yet cover google.maps.places.AutocompleteSuggestion
// or the new Place class. The minimal types below mirror the documented surface.
// See: https://developers.google.com/maps/documentation/javascript/reference/place
//      https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data

type AutocompleteSuggestionRequest = {
  input: string
  includedRegionCodes?: string[]
  language?: string
  sessionToken?: AutocompleteSessionTokenInstance
}

type PlacePredictionText = { text: string }

type PlacePrediction = {
  placeId: string
  text?: PlacePredictionText
  structuredFormat?: {
    mainText?: PlacePredictionText
    secondaryText?: PlacePredictionText
  }
  toPlace?: () => PlaceInstance
}

type AutocompleteSessionTokenInstance = object

type AutocompleteSessionTokenConstructor = new () => AutocompleteSessionTokenInstance

type AutocompleteSuggestionResult = {
  placePrediction?: PlacePrediction
}

type AutocompleteSuggestionStatic = {
  fetchAutocompleteSuggestions(
    req: AutocompleteSuggestionRequest,
  ): Promise<{ suggestions: AutocompleteSuggestionResult[] }>
}

type PlaceFetchFieldsRequest = { fields: string[] }

type PlaceAddressComponent = {
  types?: string[]
  longText?: string
  shortText?: string
}

type PlaceInstance = {
  id?: string
  displayName?: string
  location?: google.maps.LatLng
  addressComponents?: PlaceAddressComponent[]
  fetchFields(req: PlaceFetchFieldsRequest): Promise<unknown>
}

type PlaceConstructor = new (opts: { id: string }) => PlaceInstance

type PlacesNewNamespace = {
  AutocompleteSuggestion: AutocompleteSuggestionStatic
  Place: PlaceConstructor
  AutocompleteSessionToken: AutocompleteSessionTokenConstructor
}
